/** Follows href through a temporary hidden anchor, always removing it afterwards. */
function clickHiddenLink(href: string, filename?: string): void {
  const link = document.createElement('a');
  link.href = href;
  if (filename) link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  try {
    link.click();
  } finally {
    document.body.removeChild(link);
  }
}

/** Saves a blob to disk via a temporary anchor, always cleaning up after itself. */
function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    clickHiddenLink(url, filename);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Downloads a stored file from a short-lived link that names it, without leaving the page. */
export function downloadStoredFile(url: string): void {
  clickHiddenLink(url);
}

/**
 * Triggers a browser file download from a base64-encoded payload.
 * Always removes the temporary anchor and revokes the object URL.
 */
export function downloadBase64File(base64: string, filename: string, mimeType: string): void {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  saveBlob(new Blob([bytes], { type: mimeType }), filename);
}

/**
 * Downloads a file that the server renders on demand.
 *
 * The bytes are fetched rather than navigated to, for two reasons: a failed
 * render surfaces as a thrown error the caller can display instead of replacing
 * the app with an error page, and the caller's progress indicator stays up for
 * the whole server-side render. Rejects if the server does not return 2xx.
 */
export async function downloadFileFromUrl(url: string, filename: string): Promise<void> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`download request failed with status ${response.status}`);
  }
  saveBlob(await response.blob(), filename);
}
